import { IPOSForage } from '@context/app-context';
import { IEntriesListBody } from '@modules/front-office/interface/entries-management';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dateTimeFormat } from '@utils/format';
import moment from 'moment';
import { PrinterDetails } from '../interface/bo-interface/printers.interface';
import { StationMenusResult } from '../interface/station-menu.interface';

export interface Location {
  _id: number;
  caption: string;
  zip_code: string | null;
}

export interface IPOSShift {
  workPeriod: string;
  stationName: string;
  workPeriodID: string;
}

interface IMenuState {
  printers: PrinterDetails[];
  menus: StationMenusResult[];
  menuImages: {
    menuID: string;
    imageID: string;
    fileImage?: Blob;
    expireAt?: string;
  }[]; // for multiple image in future imageID: string[]
  locations: Location[];
  shifts: IPOSShift[];
  billOutEntries: Record<
    string,
    {
      salesGroup: string;
      clearingCategory: string;
      items: IEntriesListBody[];
    }
  >;
  isLoadingMenuImages: boolean;
}

const initialState: IMenuState = {
  menus: [],
  menuImages: [],
  printers: [],
  locations: [],
  shifts: [],
  billOutEntries: {},
  isLoadingMenuImages: false,
};

export const clearPOSMenuImagesThunk = createAsyncThunk<
  void,
  { stationID: string; posForage: IPOSForage } // stationID
>('posMasterlist/clearPOSMenuImages', async ({ stationID, posForage }) => {
  if (!stationID) return;

  await posForage.clearStore(stationID);
});

export const POSMasterlistSlice = createSlice({
  name: 'posMasterlist',
  initialState,
  reducers: {
    // MENU
    setPOSMenu: (state, action: PayloadAction<StationMenusResult[]>) => {
      state.menus = action.payload;
    },
    setPOSMenuImages: (
      state,
      action: PayloadAction<
        { menuID: string; imageID: string; fileImage?: Blob }[]
      >,
    ) => {
      action.payload.forEach(({ menuID, imageID, fileImage }) => {
        const index = state.menuImages.findIndex(
          (item) => item.menuID === menuID,
        );

        if (index === -1) {
          state.menuImages.push({
            menuID,
            imageID,
            ...(fileImage && { fileImage }),
            expireAt: moment().add(12, 'hours').format(dateTimeFormat),
          });
        } else {
          state.menuImages[index] = {
            ...state.menuImages[index],
            imageID,
            expireAt: moment().add(12, 'hours').format(dateTimeFormat),
            ...(fileImage && { fileImage }),
          };
        }
      });
    },
    setPOSMenuFileImage: (
      state,
      { payload }: PayloadAction<{ menuID: string; fileImage: Blob }>,
    ) => {
      const { menuID, fileImage } = payload;

      const index = state.menuImages.findIndex(
        (item) => item.menuID === menuID,
      );

      if (index === -1) {
        state.menuImages.push({
          menuID,
          imageID: '',
          fileImage,
          expireAt: moment().add(12, 'hours').format(dateTimeFormat),
        });
        return;
      }

      state.menuImages[index] = {
        ...state.menuImages[index],
        expireAt: moment().add(12, 'hours').format(dateTimeFormat),
        fileImage,
      };
    },
    setPOSMenuLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoadingMenuImages = payload;
    },

    // LOCATION
    setPOSLocations: (state, action: PayloadAction<Location[]>) => {
      state.locations = action.payload;
    },
    addPOSLocation: (state, action: PayloadAction<Location>) => {
      state.locations.push(action.payload);
    },

    // SHIFTS
    setPOSShifts: (state, action: PayloadAction<IPOSShift[]>) => {
      state.shifts = action.payload;
    },

    // ENTRIES
    setPOSBillOutEntries: (
      state,
      action: PayloadAction<IEntriesListBody[]>,
    ) => {
      const entries = action.payload;

      const filteredData = entries.reduce(
        (prev, cur) => {
          const { salesGroup, clearingCategory, isActive } = cur;

          const groupKey =
            salesGroup === 'E-Payment' ? salesGroup : clearingCategory;

          if (!isActive || !groupKey) return prev;

          if (!prev[groupKey]) {
            prev[groupKey] = {
              salesGroup,
              clearingCategory,
              items: [],
            };
          }

          prev[groupKey].items.push(cur);
          return prev;
        },
        {} as Record<
          string,
          {
            salesGroup: string;
            clearingCategory: string;
            items: IEntriesListBody[];
          }
        >,
      );

      state.billOutEntries = filteredData;
    },

    // PRINTERS
    setPOSPrinters: (state, action: PayloadAction<PrinterDetails[]>) => {
      state.printers = action.payload;
    },

    // ALL
    clearPOSMasterlist: (state, action: PayloadAction<boolean | undefined>) => {
      if (action.payload) {
        return initialState;
      }
      return {
        ...initialState,
        shifts: state.shifts,
        printers: state.printers,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearPOSMenuImagesThunk.fulfilled, (state) => {
      state.menuImages = [];
    });
  },
});

export const {
  setPOSMenu,
  clearPOSMasterlist,
  setPOSLocations,
  addPOSLocation,
  setPOSShifts,
  setPOSBillOutEntries,
  setPOSPrinters,
  setPOSMenuImages,
  setPOSMenuFileImage,
  setPOSMenuLoading,
} = POSMasterlistSlice.actions;
export default POSMasterlistSlice.reducer;
